<!-- GeographicStateSelect.vue -->
<script lang="ts" setup>
import { onBeforeMount, onUnmounted, reactive } from 'vue';
import { CountryCode } from '../../Enums/ListEnums';
import { IListItem } from '../../lib/Api/List';
import { initPinia } from '../../lib/PiniaMgr';
import { useListStore } from '../../lib/Stores/ListStore';
import { DisplayStyle, HtmlChangeEventType } from '../../Enums/UiEnums';
import ListSelect from './ListSelect.vue';
import MessageBadge from '../Messages/MessageBadge.vue';
import MessageText from '../Messages/MessageText.vue';
import { ValidationState } from '../../lib/Types/Types';

    const props = withDefaults(defineProps<{
        modelValue: string|undefined,
        usStates?: boolean,
        canadianProvinces?: boolean,
        required?: boolean,
        disabled?: boolean,
        invalidValueText?: string
    }>(), {
        usStates: true,
        canadianProvinces: false,
    });    

    const emit = defineEmits<{
        (e: "update:modelValue", strValue: string|undefined): void,
        (e: "validationState", state: ValidationState, msg?: string): void
    }>();

    defineExpose({performValidation});

    const requiredText = "Required";

    const listStore = useListStore(initPinia());

    const compData = reactive<{
        options?: IListItem[],
        currentSelection?: string|undefined
    }>({});

    onBeforeMount(async () => {
        compData.currentSelection = props.modelValue;

        const options = (await listStore.getGeographicalStates())?.filter(item => {
                if (props.usStates && props.canadianProvinces) {
                    return true;
                } 
                
                if (props.usStates) {
                    return item.country_code === CountryCode.USA;
                } 

                if (props.canadianProvinces) {
                    return item.country_code === CountryCode.CAN;
                }
            }).map<IListItem>(item => 
                ({
                    id: item.state_code,
                    displayText: item.state_name,
                    isHeader: false,
                    data: item
                }));

            compData.options = options;
        });
    onUnmounted(() => {
        emit("validationState", "unmounted");
    });

    function onChangeSelection(value: string|undefined) 
    {
        compData.currentSelection = value;
        performValidation();
        emit("update:modelValue", value);
    }; 

    function performValidation(eventType?: HtmlChangeEventType)
    {
        let isValidLocal: boolean = true;
        let validationErrorMsgLocal: string|undefined;

        // Make sure we check if we have any data!        
        if (props.required)
        {
            if (undefined == compData.currentSelection)
            {
                isValidLocal = false;
                validationErrorMsgLocal = requiredText;
            }
        }
        emit("validationState", isValidLocal, props.invalidValueText ?? validationErrorMsgLocal);         
    }
    
</script>

<template>
    <div>
        <ListSelect
            :modelValue="compData.currentSelection"
            @update:model-value="onChangeSelection"
            :disabled="props.disabled"
            :options="compData.options"
            placeholder="Select a state"
            :searchable="true"
            :clearable="true">
            <template #option="{option}">
                {{ (<IListItem>option).displayText }}
                <template v-if="'CAN' === option.data?.country_code">
                    <MessageText :displayStyle="DisplayStyle.Danger">
                        <i class="fab fa-canadian-maple-leaf"></i>
                    </MessageText>
                </template>
                <template v-if="option.data?.is_territory">
                    <MessageBadge :displayStyle="DisplayStyle.Info">
                        Territory
                    </MessageBadge>
                </template>
                <template v-if="option.data?.is_special_government_address">
                    <MessageBadge :displayStyle="DisplayStyle.Secondary">
                        Mil/Gov
                    </MessageBadge>
                </template>
            </template>
        </ListSelect>
    </div>
</template>